// NEW_FILE_JUSTIFIED: New dialog component for manually creating leads — no existing equivalent
import { useCallback } from 'react';
import { useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { UserPlus, CheckCircle2, AlertCircle } from 'lucide-react';
import { useCreateLeadForm } from '@/hooks/useCreateLeadForm';
import type { ICreateLeadDialogProps } from '@/@types/leads';

export function CreateLeadDialog({
  open,
  onOpenChange,
  prefillName,
  prefillEmail,
  prefillPhone,
  prefillSubject,
  prefillSource,
  aiExtras,
  matchedServices: matchedServicesProp,
  unmatchedServices,
  orgServiceTypes,
  onCreated,
}: ICreateLeadDialogProps) {
  const createLead = useMutation(api.leads.createLead);
  const createServiceData = useMutation(api.leadServiceData.create);
  const {
    form,
    setField,
    resetForm,
    isSubmitting,
    setIsSubmitting,
    aiExtras: extras,
    matchedServices,
  } = useCreateLeadForm(
    prefillName,
    prefillEmail,
    prefillSubject,
    prefillPhone,
    prefillSource,
    aiExtras,
    matchedServicesProp
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.name.trim()) {
        toast.error('Name is required');
        return;
      }

      setIsSubmitting(true);
      try {
        const leadId = await createLead({
          name: form.name.trim(),
          phoneNumber: form.phoneNumber.trim() || undefined,
          email: form.email.trim() || undefined,
          source: form.source || undefined,
          leadType: extras?.leadType,
          temperature: extras?.temperature,
          serviceInterest: extras?.serviceInterest,
          estimatedValue: extras?.estimatedValue,
        });

        // Create leadServiceData entries for each matched service
        if (matchedServices && matchedServices.length > 0 && orgServiceTypes) {
          for (const matched of matchedServices) {
            const serviceType = orgServiceTypes.find(st => st.name === matched.serviceTypeName);
            if (!serviceType) continue;

            try {
              const collectedFields = matched.extractedFields.map(f => ({
                fieldLabel: f.fieldLabel,
                value: f.value,
                ...(f.storageId ? { storageIds: [f.storageId] } : {}),
              }));

              // Add default Duration and Provider for car/motorbike insurance
              const insuranceDefaults = ['car insurance', 'motorbike insurance'];
              if (insuranceDefaults.includes(matched.serviceTypeName.toLowerCase())) {
                if (!collectedFields.some(f => f.fieldLabel === 'Duration (months)')) {
                  collectedFields.push({ fieldLabel: 'Duration (months)', value: '12' });
                }
                if (!collectedFields.some(f => f.fieldLabel === 'Provider')) {
                  collectedFields.push({ fieldLabel: 'Provider', value: 'AIG' });
                }
              }

              await createServiceData({
                leadId,
                orgId: serviceType.orgId as Id<'organisations'>, // AS_JUSTIFIED: orgId from useQuery result typed as string in ICreateLeadDialogProps
                serviceTypeId: serviceType._id as Id<'orgServiceTypes'>, // AS_JUSTIFIED: _id from useQuery result typed as string in ICreateLeadDialogProps
                collectedFields,
              });
            } catch {
              console.error(`Failed to create service data for ${matched.serviceTypeName}`);
            }
          }
        }

        toast.success('Lead created successfully');
        resetForm();
        onOpenChange(false);
        onCreated?.();
      } catch (error: unknown) {
        // Show duplicate lead warning from ConvexError
        const msg =
          error instanceof Error && 'data' in error
            ? String((error as Error & { data: unknown }).data) // AS_JUSTIFIED: ConvexError always has .data field containing the message string
            : undefined;
        if (msg && msg.includes('already exists')) {
          toast.warning(msg);
        } else {
          toast.error('Failed to create lead');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      form,
      extras,
      matchedServices,
      orgServiceTypes,
      createLead,
      createServiceData,
      resetForm,
      onOpenChange,
      onCreated,
      setIsSubmitting,
    ]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Create Lead
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="lead-name" className="text-sm font-medium text-foreground">
              Name *
            </label>
            <Input
              id="lead-name"
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              placeholder="Lead name"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="lead-phone" className="text-sm font-medium text-foreground">
              Phone Number
            </label>
            <Input
              id="lead-phone"
              value={form.phoneNumber}
              onChange={e => setField('phoneNumber', e.target.value)}
              placeholder="+1 234 567 8900"
            />
            <p className="text-xs text-muted-foreground">
              WhatsApp and Telegram will be detected automatically
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="lead-email" className="text-sm font-medium text-foreground">
              Email
            </label>
            <Input
              id="lead-email"
              type="email"
              value={form.email}
              onChange={e => setField('email', e.target.value)}
              placeholder="lead@example.com"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="lead-source" className="text-sm font-medium text-foreground">
              Source
            </label>
            <Select value={form.source} onValueChange={val => setField('source', val)}>
              <SelectTrigger id="lead-source">
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="website">Website</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="telegram">Telegram</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="organic">Organic</SelectItem>
                <SelectItem value="ad">Ad</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Matched Services Preview */}
          {matchedServices && matchedServices.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Detected Services</p>
                <div className="space-y-2">
                  {matchedServices.map((service, idx) => {
                    const serviceType = orgServiceTypes?.find(
                      st => st.name === service.serviceTypeName
                    );
                    const totalFields = serviceType?.requiredFieldCount ?? 0;
                    const extractedCount = service.extractedFields.length;

                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between rounded-md border border-border bg-muted/50 px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span className="text-sm font-medium">{service.serviceTypeName}</span>
                          <Badge variant="secondary" className="text-xs">
                            {service.confidence}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {extractedCount}/{totalFields} fields
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Unmatched Services */}
          {unmatchedServices && unmatchedServices.length > 0 && (
            <div className="space-y-1">
              {unmatchedServices.map((service, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <AlertCircle className="h-3 w-3" />
                  <span>{service} (not offered)</span>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Lead'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
